import { useState } from 'react';
import { Bell, Clock, Calendar, Check, AlarmClock, Edit, Trash2, AlertCircle, Flag, MoreVertical, X } from 'lucide-react';
import { useReminders, Reminder } from '../../context/RemindersContext';
import { useTheme } from '../../context/ThemeContext';
import { usePets } from '../../context/PetsContext';
import { Button } from '../ui/button';
import { toast } from 'sonner';

interface ReminderCardProps {
  reminder: Reminder;
  onClick?: () => void;
  compact?: boolean;
}

export function ReminderCard({ reminder, onClick, compact = false }: ReminderCardProps) {
  const { colors, theme } = useTheme();
  const { markAsCompleted, snoozeReminder, deleteReminder } = useReminders();
  const { pets } = usePets();
  const [showActions, setShowActions] = useState(false);

  const pet = reminder.petId ? pets.find(p => p.id === reminder.petId) : null;

  const getCategoryConfig = (category: string) => {
    const configs = {
      vaccination: { emoji: '💉', color: '#EF4444', label: 'Vaccination' },
      'health-checkup': { emoji: '🏥', color: '#10B981', label: 'Health Checkup' },
      medication: { emoji: '💊', color: '#8B5CF6', label: 'Medication' },
      grooming: { emoji: '✂️', color: '#EC4899', label: 'Grooming' },
      'follow-up': { emoji: '📋', color: '#F59E0B', label: 'Follow-up' },
      'document-renewal': { emoji: '📄', color: '#3B82F6', label: 'Document' },
      custom: { emoji: '📌', color: '#6B7280', label: 'Custom' }
    };
    return configs[category as keyof typeof configs] || configs.custom;
  };

  const getPriorityConfig = (priority: string) => {
    const configs = {
      normal: { color: '#6B7280', label: 'Normal', bg: theme === 'dark' ? 'rgba(107, 114, 128, 0.15)' : '#F3F4F6' },
      important: { color: '#F59E0B', label: 'Important', bg: theme === 'dark' ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7' },
      critical: { color: '#EF4444', label: 'Critical', bg: theme === 'dark' ? 'rgba(239, 68, 68, 0.15)' : '#FEE2E2' }
    };
    return configs[priority as keyof typeof configs] || configs.normal;
  };

  const getStatusConfig = (status: string) => {
    const configs = {
      'upcoming': { color: theme === 'dark' ? '#60A5FA' : '#3B82F6', label: 'Upcoming' },
      'due-today': { color: theme === 'dark' ? '#FCD34D' : '#F59E0B', label: 'Due Today' },
      'overdue': { color: theme === 'dark' ? '#FCA5A5' : '#EF4444', label: 'Overdue' },
      'completed': { color: theme === 'dark' ? '#86EFAC' : '#10B981', label: 'Completed' },
      'snoozed': { color: theme === 'dark' ? '#C7D2FE' : '#6366F1', label: 'Snoozed' }
    };
    return configs[status as keyof typeof configs] || configs.upcoming;
  };

  const categoryConfig = getCategoryConfig(reminder.category);
  const priorityConfig = getPriorityConfig(reminder.priority);
  const statusConfig = getStatusConfig(reminder.status);

  const handleMarkComplete = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAsCompleted(reminder.id);
    toast.success('Reminder marked as completed');
    setShowActions(false);
  };

  const handleSnooze = (e: React.MouseEvent) => {
    e.stopPropagation();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    snoozeReminder(reminder.id, tomorrow.toISOString());
    toast.success('Reminder snoozed until tomorrow');
    setShowActions(false);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this reminder?')) {
      deleteReminder(reminder.id);
      toast.success('Reminder deleted');
    }
    setShowActions(false);
  };

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="p-2 rounded-lg border-l-4 cursor-pointer transition-all hover:shadow-sm"
        style={{
          borderLeftColor: categoryConfig.color,
          backgroundColor: colors.cardBgSecondary,
          borderTop: `1px solid ${colors.border}`,
          borderRight: `1px solid ${colors.border}`,
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <div className="flex items-center gap-2">
          <span className="text-sm">{categoryConfig.emoji}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate" style={{ color: colors.heading, fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>
              {reminder.title}
            </div>
            <div className="text-xs" style={{ color: colors.textSecondary }}>
              {reminder.time}
            </div>
          </div>
          {reminder.priority !== 'normal' && (
            <Flag className="w-3 h-3 flex-shrink-0" style={{ color: priorityConfig.color }} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      className="relative p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg"
      style={{
        borderColor: reminder.status === 'overdue' ? statusConfig.color : colors.border,
        backgroundColor: colors.cardBg,
        borderStyle: reminder.status === 'completed' ? 'dashed' : 'solid',
        opacity: reminder.status === 'completed' ? 0.6 : 1
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3 flex-1">
          <div
            className="p-2 rounded-lg flex-shrink-0"
            style={{ backgroundColor: `${categoryConfig.color}20` }}
          >
            <span className="text-lg">{categoryConfig.emoji}</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="truncate" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600, fontSize: '16px', color: colors.heading }}>
              {reminder.title}
            </h3>
            {pet && (
              <div className="flex items-center gap-1 mt-1">
                <span className="text-xs" style={{ color: colors.textSecondary }}>
                  {pet.name}
                </span>
              </div>
            )}
          </div>
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowActions(!showActions);
          }}
          className="p-1 rounded-lg transition-colors flex-shrink-0"
          style={{ backgroundColor: 'transparent' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          <MoreVertical className="w-4 h-4" style={{ color: colors.textSecondary }} />
        </button>
      </div>

      {/* Quick Actions Dropdown */}
      {showActions && (
        <div
          className="absolute right-4 top-14 rounded-xl shadow-xl z-10 py-2 min-w-[160px]"
          style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}` }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleMarkComplete}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
            style={{ color: colors.text }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Check className="w-4 h-4" />
            Mark as Done
          </button>
          <button
            onClick={handleSnooze}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
            style={{ color: colors.text }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <AlarmClock className="w-4 h-4" />
            Snooze
          </button>
          <button
            onClick={handleDelete}
            className="w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors"
            style={{ color: '#EF4444' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = colors.cardBgSecondary;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      )}

      {/* Date & Time */}
      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4" style={{ color: colors.textSecondary }} />
          <span className="text-sm" style={{ color: colors.text }}>
            {new Date(reminder.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4" style={{ color: colors.textSecondary }} />
          <span className="text-sm" style={{ color: colors.text }}>
            {reminder.time}
          </span>
        </div>
      </div>

      {/* Status & Priority */}
      <div className="flex items-center gap-2 mb-3">
        <div
          className="px-2 py-1 rounded-lg text-xs"
          style={{
            backgroundColor: statusConfig.color + '20',
            color: statusConfig.color,
            fontFamily: 'Sora, sans-serif',
            fontWeight: 600
          }}
        >
          {statusConfig.label}
        </div>
        {reminder.priority !== 'normal' && (
          <div
            className="px-2 py-1 rounded-lg text-xs flex items-center gap-1"
            style={{
              backgroundColor: priorityConfig.bg,
              color: priorityConfig.color,
              fontFamily: 'Sora, sans-serif',
              fontWeight: 600
            }}
          >
            <Flag className="w-3 h-3" />
            {priorityConfig.label}
          </div>
        )}
        {reminder.repeat !== 'one-time' && (
          <div
            className="px-2 py-1 rounded-lg text-xs"
            style={{
              backgroundColor: colors.cardBgSecondary,
              color: colors.textSecondary
            }}
          >
            Repeats {reminder.repeat}
          </div>
        )}
      </div>

      {/* Notes */}
      {reminder.notes && (
        <div className="text-sm" style={{ color: colors.textSecondary }}>
          {reminder.notes}
        </div>
      )}

      {/* Auto-generated badge */}
      {reminder.autoGenerated && (
        <div className="mt-3 flex items-center gap-1 text-xs" style={{ color: colors.textSecondary }}>
          <AlertCircle className="w-3 h-3" />
          Auto-generated reminder
        </div>
      )}
    </div>
  );
}